// SPEC-13 · Splash. Violet, the cream logo centred, the tagline at the
// bottom. It covers the session check and takes no input; decideEntry
// holds the timing rules.
//
// SPEC-52: a launch with a session reads her account's language, as a
// sign-in does, so a language the team set from the admin applies at this
// opening. The words change once read; the screen she lands on is the same.

import { useEffect } from "react";

import { PyfLogo } from "../components/PyfLogo";
import { decideEntry } from "../entry/decideEntry";
import type { SessionCheck } from "../entry/session";
import { useCopy, useLanguage } from "../i18n/LanguageProvider";
import { lastPlace } from "../lib/place";
import { paths, useRouter } from "../lib/router";

export function Splash({ checkSession }: { checkSession: SessionCheck }) {
  const { navigate } = useRouter();
  const words = useCopy();
  const { syncWithAccount } = useLanguage();

  useEffect(() => {
    let active = true;
    void decideEntry(checkSession).then((entry) => {
      if (!active) return;
      const signedIn = entry === "signedIn";
      if (signedIn) syncWithAccount();
      // The app reopens where it was left; only a sign-in opens Conversa (the founder's answer 7 on SPEC-27).
      // Replace, so the back gesture never returns to the Splash.
      navigate(signedIn ? (lastPlace() ?? paths.conversa) : paths.welcome, { replace: true });
    });
    return () => {
      active = false;
    };
  }, [checkSession, navigate, syncWithAccount]);

  return (
    <main
      aria-busy="true"
      aria-label={words.splash.loading}
      className="relative flex min-h-dvh flex-col items-center justify-center bg-violet"
    >
      <PyfLogo className="w-[168px] text-cream-deep" />
      <p className="absolute bottom-14 text-label font-semibold tracking-[var(--ls-label)] text-cream uppercase">
        {words.brand.tagline}
      </p>
    </main>
  );
}
